<template>
  <v-sheet class="py-6 px-8">
    <p class="text-h6 font-weight-bold grey--text text--darken-3 text-center mb-6">옵션 상품 수정하기</p>

    <v-sheet class="mt-2 px-4">
      <!-- 코드 (읽기 전용) -->
      <v-text-field class="rounded-10" label="코드" dense outlined v-model="update.code" readonly disabled prepend-inner-icon="mdi-lock"></v-text-field>

      <!-- 가격 -->
      <v-text-field class="rounded-10" label="가격 (원)" dense outlined v-model.number="update.price" type="number" :rules="[rules.required, rules.priceValid]" @input="validatePrice"></v-text-field>

      <!-- 제목 섹션 -->
      <div class="font-weight-bold text-h6 ml-4 mt-4 mb-2">제목</div>

      <!-- 한글 제목 -->
      <v-text-field class="rounded-10" label="제목 (한글)" dense outlined v-model.trim="update.title.kr" :counter="30" :rules="[rules.required, rules.titleLength]"></v-text-field>

      <!-- 영어 제목 -->
      <v-text-field class="rounded-10" label="제목 (영어)" dense outlined v-model.trim="update.title.en" :counter="30" :rules="[rules.titleLength]"></v-text-field>

      <!-- 일본어 제목 -->
      <v-text-field class="rounded-10" label="제목 (일본어)" dense outlined v-model.trim="update.title.jp" :counter="30" :rules="[rules.titleLength]"></v-text-field>

      <!-- 설명 섹션 -->
      <div class="font-weight-bold text-h6 ml-4 mt-4 mb-2">설명</div>

      <!-- 한글 설명 -->
      <v-textarea class="rounded-10" label="부제목 (한글)" dense outlined v-model.trim="update.subTitle.kr" :counter="200" :rules="[rules.required, rules.subTitleLength]" rows="3"></v-textarea>

      <!-- 영어 설명 -->
      <v-textarea class="rounded-10" label="부제목 (영어)" dense outlined v-model.trim="update.subTitle.en" :counter="200" :rules="[rules.subTitleLength]" rows="3"></v-textarea>

      <!-- 일본어 설명 -->
      <v-textarea class="rounded-10" label="부제목 (일본어)" dense outlined v-model.trim="update.subTitle.jp" :counter="200" :rules="[rules.subTitleLength]" rows="3"></v-textarea>

      <!-- 이벤트 페이지 선택 섹션 -->
      <div class="font-weight-bold text-h6 ml-4 mt-4 mb-2">상세 설명 페이지</div>

      <!-- 이벤트 페이지 선택 다이얼로그 -->
      <EventPageSelector 
        v-model="update.descriptionCode"
        @select="onEventPageSelected"
        @clear="onEventPageCleared"
      />
    </v-sheet>

    <!-- 버튼 -->
    <v-sheet class="d-flex justify-center mt-5">
      <v-btn class="rounded-10 px-6 mr-4" color="grey" large dark depressed @click="$emit('close')">
        <v-icon small left>mdi-cancel</v-icon>
        취소하기
      </v-btn>
      <v-btn class="rounded-10 px-6" color="green" large dark depressed @click="updateSubmit()">
        <v-icon small left>mdi-database-edit-outline</v-icon>
        수정하기
      </v-btn>
    </v-sheet>
  </v-sheet>
</template>

<script>
import optionProductService from "@/api/optionProductService"
import EventPageSelector from "./EventPageSelector"

export default {
  components: {
    EventPageSelector,
  },
  props: {
    updateItem_import: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    update: {
      id: null,
      code: "",
      title: {
        kr: "",
        en: "",
        jp: "",
      },
      subTitle: {
        kr: "",
        en: "",
        jp: "",
      },
      price: 0,
      descriptionCode: null,
    },

    // Validation rules
    rules: {
      required: (value) => !!value || "필수 입력 항목입니다.",
      codeLength: (value) => (value && value.length <= 20) || "코드는 20자 이내여야 합니다.",
      titleLength: (value) => !value || value.length <= 30 || "제목은 30자 이내여야 합니다.",
      subTitleLength: (value) => !value || value.length <= 200 || "설명은 200자 이내여야 합니다.",
      priceValid: (value) => value >= 0 || "가격은 0원 이상이어야 합니다.",
    },

    // Selected event page info
    selectedEventPage: null,
  }),

  mounted() {
    this.loadProductData()
  },

  watch: {
    updateItem_import: {
      handler() {
        this.loadProductData()
      },
      deep: true,
    },
  },

  methods: {
    // 이벤트 페이지 선택 핸들러
    onEventPageSelected(page) {
      this.selectedEventPage = page
      console.log("선택된 이벤트 페이지:", page)
    },

    // 이벤트 페이지 선택 해제 핸들러
    onEventPageCleared() {
      this.selectedEventPage = null
    },

    // Load product data for editing
    async loadProductData() {
      if (!this.updateItem_import || !this.updateItem_import.id) return

      try {
        // Load full product details from API
        const product = await optionProductService.getOptionProduct(this.updateItem_import.id)

        this.update.id = product.id
        this.update.code = product.code
        this.update.price = product.price
        this.update.descriptionCode = product.descriptionCode

        // Parse JSON fields
        try {
          this.update.title = typeof product.title === "string" ? JSON.parse(product.title) : product.title || { kr: "", en: "", jp: "" }
        } catch (e) {
          this.update.title = { kr: product.title || "", en: "", jp: "" }
        }

        try {
          this.update.subTitle = typeof product.subTitle === "string" ? JSON.parse(product.subTitle) : product.subTitle || { kr: "", en: "", jp: "" }
        } catch (e) {
          this.update.subTitle = { kr: product.subTitle || "", en: "", jp: "" }
        }
      } catch (error) {
        console.error("상품 정보 로드 오류:", error)
        alert("상품 정보를 불러오는데 실패했습니다")
      }
    },

    // Validate price input
    validatePrice() {
      if (this.update.price < 0) {
        this.update.price = 0
      }
    },

    // Submit form
    updateSubmit: _.debounce(async function () {
      // Validate required fields (코드 제외 - 수정 불가)
      if (!this.update.title.kr) {
        alert("한글 제목을 입력해주세요")
        return
      }

      if (!this.update.subTitle.kr) {
        alert("한글 설명을 입력해주세요")
        return
      }

      if (this.update.price < 0) {
        alert("유효한 가격을 입력해주세요")
        return
      }

      // Check title lengths
      if (this.update.title.kr.length > 30 || (this.update.title.en && this.update.title.en.length > 30) || (this.update.title.jp && this.update.title.jp.length > 30)) {
        alert("제목은 30자 이내여야 합니다")
        return
      }

      // Check subtitle lengths
      if (this.update.subTitle.kr.length > 200 || (this.update.subTitle.en && this.update.subTitle.en.length > 200) || (this.update.subTitle.jp && this.update.subTitle.jp.length > 200)) {
        alert("설명은 200자 이내여야 합니다")
        return
      }

      try {
        // Submit to API (코드 수정 불가)
        await optionProductService.updateOptionProduct(this.update.id, {
          title: JSON.stringify(this.update.title),
          subTitle: JSON.stringify(this.update.subTitle),
          price: Number(this.update.price),
          descriptionCode: this.update.descriptionCode || null,
        })

        alert("정상적으로 수정되었습니다.")
        this.$emit("close")
      } catch (error) {
        console.error("옵션 상품 수정 오류:", error)
        alert(error.message || "수정 중 오류가 발생했습니다")
      }
    }, 500),
  },
}
</script>

<style scoped></style>
